/**
 * Compose, from design/canvas/Composer.src.html: the brief bar parses a prompt into filled slots; each
 * slot card carries its poster, a waveform trimmer over the snippet's bounds, its own sound's speaker and
 * gain, and, with the ASMR channel on, its sound effect's lane; the audio desk sits beneath, then text.
 * Preview chains proxies client-side and mixes every audio channel live in the browser (Dax,
 * 2026-09-29); only burned text renders a picture draft. Export queues the real FFmpeg cut.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { AUTOSAVE_DEBOUNCE_MS, COMPOSE_REMIX_TARGETS, CaptionStyle, REMIX_PRESETS, RemixAudio as RemixAudioSchema, remixAudioDefault, channelHeard, OUTLINE_STOPS, TEXT_FONTS, type DraftSlot, type ComposeRemixTarget, type RemixAudio, type TextLine, type VideoFormat } from '@studio/contracts';
import { AspectPeek, useAspectPeek } from '@/components/aspect-peek';
import { AutoPill } from '@/components/auto-marker';
import { Chevron, Icon, Spark } from '@/components/icons';
import { ComposeRemixModal } from '@/components/compose-remix-modal';
import { ExtendModal } from '@/components/extend-modal';
import { FreshnessBadge, FreshnessDot } from '@/components/freshness-badge';
import { Knob } from '@/components/knob';
import { PlatformMark } from '@/components/platform-mark';
import { RenderPill } from '@/components/render-pill';
import { PreviewScrubber } from '@/components/preview-scrubber';
import { Recents } from '@/components/recent-files';
import { RowMenu } from '@/components/row-menu';
import { SaveStatus } from '@/components/save-status';
import { TopBar } from '@/components/shell';
import { SlotThumb } from '@/components/slot-thumb';
import { SoundLane } from '@/components/sound-lane';
import { ratingMenuItem } from '@/components/star-rating';
import { TextBuilder } from '@/components/text-builder';
import { TextOverlay, TextPreview } from '@/components/text-preview';
import { Tool, ToolDivider, ToolGroup, Toolbar } from '@/components/toolbar';
import { Waveform } from '@/components/waveform';
import { useRatings } from '@/hooks/use-ratings';
import { clipColours } from '@/lib/clip-hues';
import { LENGTH_SHORTFALL_MIN_MS, LENGTH_STEP_LARGE, LENGTH_STEP_SMALL, resizeSlots } from '@/lib/length';
import { queryClient, trpc } from '@/lib/trpc';
import { GAIN_MIN, GAIN_MAX, DRAFT_EDIT_QUEUE, useDraftDesk } from '@/hooks/use-draft-desk';

const FORMATS: VideoFormat[] = ['9:16', '1:1', '16:9'];
const RENDER_POLL_MS = 3000;
/** How long the cut's length stays lit after a Length step before it settles. */
const DUR_FLASH_MS = 1200;
/** How long after the last text edit the picture draft is queued. */
const TEXT_RENDER_SETTLE_MS = 1200;
/** A clip's waveform on its card, heard or not: the same height either way, so the page never moves. */
const SLOT_WAVE_H = 46;

/** What Generate is busy with, rotated in the small grey line while the plan assembles. */
const GENERATE_PHRASES = [
  'Reading the brand library themes',
  'Finding clips unused in the past 30 days',
  'Looking for the best clips in the asset library',
  'Matching clip lengths to the requested duration',
  'Placing a hook first and a closing frame',
];

function GeneratePhrase() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => x + 1), 1800);
    return () => clearInterval(t);
  }, []);
  return <>{GENERATE_PHRASES[i % GENERATE_PHRASES.length]}…</>;
}

/** Rotating prompt-entrance examples: a fresh one each visit, showing the system's range. */
const HERO_PROMPTS = [
  'Find three blonde actresses eating strawberry cheesecake and interleave them with scenes of people dancing',
  'Six chocolate clips, fast cuts under a second each, trending audio, hook first',
  'A calm 20 second morning-routine edit: pour, stir, taste, smile, pack shot to finish',
  'Four strawberry and four chocolate clips interleaved, 2 seconds each, original sound',
  'Every moment where someone laughs at the camera, cut tight, sing-along captions',
  'A product-first edit: open on the box, three process shots, end on the taste reaction',
];

/** Nearest notch on the outline ladder for a stored width. */
function outlineStopIndex(w: number | null): number {
  if (w === null) return 0;
  let best = 0;
  for (let i = 1; i < OUTLINE_STOPS.length; i++) if (Math.abs(OUTLINE_STOPS[i]! - w) < Math.abs(OUTLINE_STOPS[best]! - w)) best = i;
  return best;
}


/** The sound last chosen for a TikTok remix, per viewer: the next one offers the other (remixAudioDefault). */
const TIKTOK_AUDIO_KEY = 'studio-remix-tiktok-audio';
function lastTiktokAudio(): RemixAudio | null {
  try {
    return RemixAudioSchema.safeParse(localStorage.getItem(TIKTOK_AUDIO_KEY)).data ?? null;
  } catch {
    return null;
  }
}

/** Work in progress survives a refresh; Close (or a brand-new browser session) starts empty. */
const COMPOSE_DRAFT_KEY = 'studio-compose-draft';

export function ComposePage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_app/compose' });
  const [draftId, setDraftIdState] = useState<string | null>(() => {
    if (search.draft) return search.draft;
    try {
      return sessionStorage.getItem(COMPOSE_DRAFT_KEY);
    } catch {
      return null;
    }
  });
  const setDraftId = (id: string | null) => {
    setDraftIdState(id);
    try {
      if (id) sessionStorage.setItem(COMPOSE_DRAFT_KEY, id);
      else sessionStorage.removeItem(COMPOSE_DRAFT_KEY);
    } catch {
      // Storage unavailable (private mode); the page still works, it just will not survive a refresh.
    }
  };
  const { moveSlots, setRenderId, setPreviewUrl, audioNote, previewPlaying, stagedSlots, setStagedSlots, activeEdge, setActiveEdge, offsetDrafts, setOffsetDrafts, slipDrafts, trimDrafts, previewSlot, videoRef, altVideoRef, frontVideo, render, refresh, update, draftSave, refreshAudio, d, totalMs, asmrOn, slotCount, clipTints, playPreview, patchDraft, panelNote, offByHand, levelFresh, pausePreview, stopPreview, applySlots, setSlot, commitSlot, dragSlip, removeSlot, positionMs, seekPreview, startPreview, history, onVideoPlay, onVideoPause, audioDesk, audioPopups } = useDraftDesk({
    draftId,
    freshDraftId: search.fresh && search.draft ? search.draft : null,
    onFreshSpent: (id) => {
      if (search.fresh) void navigate({ to: '/compose', search: { draft: id }, replace: true });
    },
  });
  /** The drop commit: the visual order becomes the draft, and what was open on a card closes. */
  const commitOrder = (perm: number[]) => {
    moveSlots(perm);
    setSoundPromptOpen(null);
    setSlotPrompts({});
    setPromptOpen(null);
  };
  const [brief, setBrief] = useState('');
  // The preview is a picture draft whenever burned text is on: a text or trim edit queues a background
  // render, a spinner covers the frame, and the finished file becomes what play plays. Sound never
  // renders here: the browser mixes it live over whichever picture plays.
  const [autoRenderId, setAutoRenderId] = useState<string | null>(null);
  const { peek, showPeek } = useAspectPeek();
  const autoRenderFor = useRef<string | null>(null);
  const autoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [slotPrompts, setSlotPrompts] = useState<Record<number, string>>({});
  const [promptOpen, setPromptOpen] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState<0 | 1 | 2>(0);
  const [soundPromptOpen, setSoundPromptOpen] = useState<number | null>(null);
  const [soundPrompt, setSoundPrompt] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOrder, setDragOrder] = useState<number[] | null>(null);
  const [instructOpen, setInstructOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  // Per-clip text overlay editing: which slot's TT modal is open, and its rows while typing.
  const [textSlot, setTextSlot] = useState<number | null>(null);
  const [slotLines, setSlotLines] = useState<TextLine[]>([]);
  const textCommitRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [scriptDraft, setScriptDraft] = useState<string | null>(null);
  const [capsOpen, setCapsOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [heroPlaceholder] = useState(() => HERO_PROMPTS[Math.floor(Math.random() * HERO_PROMPTS.length)]!);

  const drafts = useQuery(trpc.compose.list.queryOptions({ templates: false, cutMode: 'exact' }));
  const templates = useQuery(trpc.compose.list.queryOptions({ templates: true }));
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [nameEdit, setNameEdit] = useState<string | null>(null);
  const captionScript = useQuery(trpc.compose.captionScript.queryOptions({ draftId: draftId ?? '' }, { enabled: draftId !== null }));
  const autoRender = useQuery(trpc.compose.renderStatus.queryOptions({ renderId: autoRenderId ?? '' }, {
    enabled: autoRenderId !== null,
    refetchInterval: (q) => (q.state.data?.status === 'done' || q.state.data?.status === 'failed' ? false : RENDER_POLL_MS),
  }));

  const plan = useMutation(trpc.compose.plan.mutationOptions({
    onSuccess: async (data) => {
      setDraftId(data.draftId);
      setRenderId(null);
      setGenerateOpen(false);
      setBrief('');
      await refresh();
    },
  }));
  const refreshSlot = useMutation(trpc.compose.refreshSlot.mutationOptions({ onSuccess: refresh }));
  const autoBrief = useMutation(trpc.compose.autoBrief.mutationOptions());
  // The Composer's Remix: the platform whose pop-up is open, and the new composition it makes.
  const [remixFor, setRemixFor] = useState<ComposeRemixTarget | null>(null);
  const remixDraft = useMutation(trpc.compose.remixDraft.mutationOptions({
    scope: { id: DRAFT_EDIT_QUEUE },
    onSuccess: async (data) => {
      setRemixFor(null);
      levelFresh.current = data.draftId;
      setDraftId(data.draftId);
      setRenderId(null);
      await refresh();
    },
  }));
  // Length: what a step could not reach by growing the clips, while the person picks how to find it; and
  // the cut's length lit while it changes.
  const [extendAsk, setExtendAsk] = useState<number | null>(null);
  const [durFlash, setDurFlash] = useState(false);
  const durFlashTimer = useRef<number | null>(null);
  const flashDuration = () => {
    setDurFlash(true);
    if (durFlashTimer.current !== null) window.clearTimeout(durFlashTimer.current);
    durFlashTimer.current = window.setTimeout(() => setDurFlash(false), DUR_FLASH_MS);
  };
  const extend = useMutation(trpc.compose.extend.mutationOptions({
    scope: { id: DRAFT_EDIT_QUEUE },
    onSuccess: async () => {
      setExtendAsk(null);
      await refresh();
      flashDuration();
    },
  }));
  const duplicate = useMutation(trpc.compose.duplicate.mutationOptions({
    onSuccess: async (data, vars) => {
      if (!vars.asTemplate) {
        setDraftId(data.draftId);
        setRenderId(null);
      }
      setSavingTemplate(false);
      setTemplateName('');
      await Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: trpc.compose.list.queryKey({ templates: true }) })]);
    },
  }));
  const instruct = useMutation(trpc.compose.instruct.mutationOptions({
    onSuccess: async () => {
      setInstruction('');
      setInstructOpen(false);
      await refresh();
    },
  }));
  const removeDraft = useMutation(trpc.compose.remove.mutationOptions({
    onSuccess: async () => {
      setDraftId(null);
      setRenderId(null);
      setBrief('');
      await queryClient.invalidateQueries({ queryKey: trpc.compose.list.queryKey({ templates: false }) });
    },
  }));
  const queueRender = useMutation(trpc.compose.render.mutationOptions({ onSuccess: (data) => setRenderId(data.renderId) }));
  const queueAutoRender = useMutation(trpc.compose.render.mutationOptions({ onSuccess: (data) => setAutoRenderId(data.renderId) }));

  // Deliberately NO auto-load of the latest draft: landing on Compose empty shows only the prompt
  // entrance; earlier work is one Load (or Archive) away.
  // Each clip and each clip's sound can be rated from its menu, as anywhere else.
  const ratingTargets = useMemo(() => (d?.slots ?? []).flatMap((s) => [
    { kind: 'snippet' as const, id: s.snippetId },
    ...(s.audio2 ? [{ kind: 'audio_bed' as const, id: s.audio2.bedId }] : []),
  ]), [d?.slots]);
  const ratings = useRatings(ratingTargets);
  const rateItem = (kind: 'snippet' | 'audio_bed', id: string) => ratingMenuItem(ratings.ratingOf({ kind, id }), (n) => ratings.rate({ kind, id }, n));

  // Burned text lives in the main preview, CapCut-style: with captions, a title card or a clip's text
  // on, the picture is re-rendered in the background; with none, the preview is the live proxies. The
  // render is the picture and the clips' own sound only, and it is keyed on what it draws (the order,
  // the trims, the text, the shape), so a level, a mute or a sound never queues one (Dax, 2026-09-29).
  // Renders COALESCE: at most one in flight, and when it lands one more is queued only if the picture
  // moved meanwhile. Without this, a typing burst stacked seven 9-second renders.
  const textActive = Boolean(d && (d.text.mode !== 'none' || d.slots.some((s) => s.overlay.length > 0)));
  const pictureKey = d ? JSON.stringify([d.format, d.text, d.slots.map((s) => [s.snippetId, s.inMs, s.outMs, s.overlay])]) : null;
  const autoInFlight = queueAutoRender.isPending
    || (autoRenderId !== null && autoRender.data?.status !== 'done' && autoRender.data?.status !== 'failed');
  useEffect(() => {
    if (!d) return;
    if (!textActive) {
      setPreviewUrl(null);
      setAutoRenderId(null);
      autoRenderFor.current = null;
      return;
    }
    if (autoRenderFor.current === pictureKey || !d.slots.length) return;
    if (autoInFlight) return; // autoInFlight is a dep: completion re-runs this and queues the latest state once
    if (autoTimer.current) clearTimeout(autoTimer.current);
    const draftId = d.id;
    const key = pictureKey;
    autoTimer.current = setTimeout(() => {
      autoRenderFor.current = key;
      queueAutoRender.mutate({ draftId, kind: 'draft' });
    }, TEXT_RENDER_SETTLE_MS);
    return () => {
      if (autoTimer.current) clearTimeout(autoTimer.current);
    };
  }, [d?.id, pictureKey, textActive, d?.slots.length, autoInFlight]);
  useEffect(() => {
    if (autoRender.data?.status === 'done' && autoRender.data.publicUrl) setPreviewUrl(autoRender.data.publicUrl);
  }, [autoRender.data?.status, autoRender.data?.publicUrl]);
  const previewBusy = Boolean(d && textActive && d.slots.length > 0 && (
    autoInFlight || autoRenderFor.current !== pictureKey
  ));


  /**
   * A Length step (2026-10-04): every clip by the same share, so the whole cut moves by it. Shortening is the
   * inverse of lengthening, so a step and its opposite come back to where they were. What growing could not
   * reach inside the snippets asks how to find the rest.
   */
  const resizeCut = (step: number) => {
    if (!d) return;
    const factor = step < 0 ? 1 + step : 1 / (1 - step);
    const { ranges, shortMs } = resizeSlots(d.slots.map((s, i) => ({ inMs: s.inMs, outMs: s.outMs, boundsInMs: d.slotMeta[i]?.boundsInMs ?? s.inMs, boundsOutMs: d.slotMeta[i]?.boundsOutMs ?? s.outMs })), factor);
    const slots = d.slots.map((s, i) => ({ ...s, ...ranges[i]! }));
    applySlots(slots);
    update.mutate({ draftId: d.id, slots });
    flashDuration();
    if (shortMs >= LENGTH_SHORTFALL_MIN_MS) {
      extend.reset();
      setExtendAsk(shortMs);
    }
  };
  const commitName = () => {
    const name = nameEdit?.trim();
    if (d && name && name !== d.name) update.mutate({ draftId: d.id, name });
    setNameEdit(null);
  };

  /**
   * Generate: with words, they are the brief; with an empty box, the system invents a brand-true
   * concept first (it lands in the box so the user sees what it came up with), then assembles.
   */
  const generate = async (forDraftId: string | null, format: VideoFormat) => {
    let text = brief.trim();
    if (!text) {
      const invented = await autoBrief.mutateAsync();
      text = invented.brief;
      setBrief(text);
    }
    plan.mutate({ brief: text, format, draftId: forDraftId });
  };
  const assembling = plan.isPending || autoBrief.isPending;
  const submitBrief = (e: FormEvent) => {
    e.preventDefault();
    if (!assembling) void generate(draftId, d?.format ?? '9:16');
  };

  /** Rows edit locally and commit debounced onto the slot, so typing never waits on the server. */
  const changeSlotLines = (lines: TextLine[]) => {
    setSlotLines(lines);
    if (textCommitRef.current) clearTimeout(textCommitRef.current);
    const si = textSlot;
    textCommitRef.current = setTimeout(() => {
      if (si !== null) setSlot(si, { overlay: lines });
    }, AUTOSAVE_DEBOUNCE_MS);
  };
  const openTextSlot = (si: number) => {
    if (textCommitRef.current) clearTimeout(textCommitRef.current);
    setTextSlot(si);
    setSlotLines(d?.slots[si]?.overlay ?? []);
  };
  const closeTextSlot = () => {
    if (textCommitRef.current) {
      clearTimeout(textCommitRef.current);
      textCommitRef.current = null;
    }
    if (d && textSlot !== null && JSON.stringify(slotLines) !== JSON.stringify(d.slots[textSlot]?.overlay ?? [])) {
      setSlot(textSlot, { overlay: slotLines });
    }
    setTextSlot(null);
  };
  const jitterSlot = (slot: DraftSlot, bounds: { boundsInMs: number; boundsOutMs: number } | null): DraftSlot => {
    const frame = 1000 / 30;
    const shift = () => Math.round((Math.random() * 12 - 6) * frame);
    const lo = bounds?.boundsInMs ?? 0;
    const hi = bounds?.boundsOutMs ?? slot.outMs + 500;
    const inMs = Math.max(lo, Math.min(slot.inMs + shift(), slot.outMs - 200));
    const outMs = Math.min(hi, Math.max(slot.outMs + shift(), inMs + 200));
    return { ...slot, inMs, outMs };
  };


  return (
    <>
      <TopBar title="Compose">
        {d && (nameEdit !== null ? (
          <input autoFocus className="crumb-edit" value={nameEdit} aria-label="Composition name" maxLength={120}
            onChange={(e) => setNameEdit(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') setNameEdit(null); }} />
        ) : (
          <span className="sub crumb compose-name edit-host">
            {d.name ?? (d.briefText.slice(0, 40) || 'untitled edit')}
            <button type="button" className="crumb-pencil" title="Rename this composition" aria-label="Rename this composition"
              onClick={() => setNameEdit(d.name ?? (d.briefText.slice(0, 40) || ''))}>
              <Icon name="pencil" />
            </button>
          </span>
        ))}
        <div className="right">
          {d && d.plan && <AutoPill>Auto-assembled · {d.slots.length} slots</AutoPill>}
        </div>
      </TopBar>
      <section className="content compose-content">
        {d && (
          <div className="toolstrip compose-toolstrip">
            <Toolbar dense>
              <Tool icon="undo" label="Undo" title="Undo the last change" disabled={!history.canUndo} onClick={history.undo} />
              <Tool icon="redo-arrow" label="Redo" title="Redo what was undone" disabled={!history.canRedo} onClick={history.redo} />
              <ToolDivider />
              <Tool icon="plus" label="New" title="Start a brand new prompt"
                onClick={() => { setDraftId(null); setRenderId(null); setBrief(''); }} />
              <SaveStatus state={draftSave} error={update.error?.message ?? null} />
              <Tool icon="folder" label="Load" title="Open a saved composition" active={loadOpen}
                onClick={() => setLoadOpen(true)} />
              <Tool icon="close" label="Close" title="Close this composition; it stays saved in the Archive"
                onClick={() => { setDraftId(null); setRenderId(null); setBrief(''); }} />
              <Tool icon="trash" label="Discard" title="Close this composition without keeping it: it leaves the Archive too" danger disabled={removeDraft.isPending}
                onClick={() => removeDraft.mutate({ draftId: d.id })} />
              <ToolDivider />
              <Tool sparkle label="Generate" title="Describe a whole new cut; every slot regenerates from the library" active={generateOpen}
                onClick={() => setGenerateOpen(true)} />
              <ToolDivider />
              <Tool icon="template" label="Template" title="Save this draft as a reusable template" active={savingTemplate}
                onClick={() => setSavingTemplate(!savingTemplate)} />
              <Tool icon="copy" label="Duplicate" title="Duplicate this draft" disabled={duplicate.isPending}
                onClick={() => duplicate.mutate({ draftId: d.id, name: null, asTemplate: false })} />
              <ToolDivider />
              <Tool icon="shuffle" label="Shuffle" title="Shuffle the clip order"
                onClick={() => commitOrder(d.slots.map((_, i) => ({ i, r: Math.random() })).sort((a, b) => a.r - b.r).map((x) => x.i))} />
              <Tool icon="jitter" label="Jitter" title="Jitter every clip's start and end a few frames"
                onClick={() => {
                  const slots = d.slots.map((s, i) => jitterSlot(s, d.slotMeta[i] ? { boundsInMs: d.slotMeta[i]!.boundsInMs, boundsOutMs: d.slotMeta[i]!.boundsOutMs } : null));
                  applySlots(slots);
                  update.mutate({ draftId: d.id, slots });
                }} />
              <Tool icon="redo" label="Refresh" title="Swap every clip for its next match" disabled={refreshSlot.isPending}
                onClick={() => { d.slots.forEach((_, i) => refreshSlot.mutate({ draftId: d.id, slotIndex: i, prompt: null })); }} />
              <ToolDivider />
              <ToolGroup label="Remix" spacing="wide">
                {COMPOSE_REMIX_TARGETS.map((t) => (
                  <Tool key={t} glyph={<PlatformMark target={t} />} title={`Remix for ${REMIX_PRESETS[t].platform}: a new composition`} active={remixFor === t}
                    disabled={!d.slots.length} onClick={() => { remixDraft.reset(); setRemixFor(t); }} />
                ))}
              </ToolGroup>
              <ToolDivider />
              <ToolGroup label="Length" spacing="tight">
                {([['minus', -LENGTH_STEP_LARGE], ['minus-sm', -LENGTH_STEP_SMALL], ['plus-sm', LENGTH_STEP_SMALL], ['plus', LENGTH_STEP_LARGE]] as const).map(([icon, step]) => (
                  <Tool key={icon} icon={icon} subtle={Math.abs(step) === LENGTH_STEP_SMALL} disabled={!d.slots.length}
                    title={`${step < 0 ? 'Shorten' : 'Lengthen'} the cut by about ${Math.round(Math.abs(step) * 100)}%, every clip alike`}
                    onClick={() => resizeCut(step)} />
                ))}
              </ToolGroup>
              <ToolDivider />
              <Tool icon="zoom" label="Zoom" title="Cycle the timeline density" active={zoomLevel > 0}
                onClick={() => setZoomLevel(((zoomLevel + 1) % 3) as 0 | 1 | 2)} />
              <ToolDivider />
              <span className="aspect-peek-holder">
                {FORMATS.map((f) => (
                  <Tool key={f} icon={`ar-${f.replace(':', '-')}`} label={f} title={`Compose in ${f}`} active={d.format === f}
                    onClick={() => { update.mutate({ draftId: d.id, format: f }); showPeek(f); }} />
                ))}
                <AspectPeek format={peek} posterUrl={d.slotMeta[0]?.posterUrl ?? null} />
              </span>
            </Toolbar>
            {savingTemplate && (
              <>
                <label className="field compose-template-name"><input autoFocus value={templateName} placeholder="Template name" aria-label="Template name" onChange={(e) => setTemplateName(e.target.value)} /></label>
                <button type="button" className="btn" disabled={!templateName.trim() || duplicate.isPending}
                  onClick={() => duplicate.mutate({ draftId: d.id, name: templateName.trim(), asTemplate: true })}>Save template</button>
              </>
            )}
            {(templates.data?.length ?? 0) > 0 && (
              <label className="field compose-template-pick">
                <select value="" aria-label="Start from template" onChange={(e) => { if (e.target.value) duplicate.mutate({ draftId: e.target.value, name: null, asTemplate: false }); }}>
                  <option value="">Start from template…</option>
                  {templates.data!.map((t) => <option key={t.id} value={t.id}>{t.name ?? 'template'}</option>)}
                </select>
              </label>
            )}
          </div>
        )}
        {d && textSlot !== null && d.slots[textSlot] && (
          <>
            <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={closeTextSlot} />
            <div className="modal text-slot-modal" role="dialog" aria-label={`Text overlay on clip ${textSlot + 1}`}>
              <button type="button" className="modal-x" aria-label="Close" onClick={closeTextSlot}><Icon name="close" /></button>
              <div className="row between text-slot-head">
                <h2>Text on clip {textSlot + 1}</h2>
                <span className="sub">Stays up for this clip&apos;s whole duration, however you trim it</span>
              </div>
              <div className="row text-slot-body">
                <TextBuilder lines={slotLines} totalMs={d.slots[textSlot]!.outMs - d.slots[textSlot]!.inMs} onChange={changeSlotLines} hideTiming />
                <div className="text-preview-col">
                  <TextPreview lines={slotLines} format={d.format}
                    proxyUrl={d.slotMeta[textSlot]?.proxyUrl ?? null}
                    frameMs={d.slots[textSlot]!.inMs} />
                  <span className="sub text-builder-note">Sharp sketch on the clip&apos;s first frame; the render burns the truth</span>
                </div>
              </div>
            </div>
          </>
        )}
        {audioPopups()}
        {d && extendAsk !== null && (
          <ExtendModal needMs={extendAsk} pending={extend.isPending} error={extend.error?.message ?? null}
            onGo={(how) => extend.mutate({ draftId: d.id, needMs: extendAsk, how })}
            onClose={() => setExtendAsk(null)} />
        )}
        {d && remixFor && (
          <ComposeRemixModal target={remixFor} initial={remixAudioDefault(remixFor, lastTiktokAudio())} pending={remixDraft.isPending}
            error={remixDraft.error?.message ?? null}
            onGo={(audio) => {
              if (remixFor === 'tiktok') {
                try {
                  localStorage.setItem(TIKTOK_AUDIO_KEY, audio);
                } catch {
                  // Storage unavailable: the next TikTok remix offers the platform's own sound first.
                }
              }
              remixDraft.mutate({ draftId: d.id, target: remixFor, audio });
            }}
            onClose={() => setRemixFor(null)} />
        )}
        {loadOpen && (
          <>
            <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setLoadOpen(false)} />
            <div className="modal picker-modal" role="dialog" aria-label="Saved compositions">
              <div className="row between"><h2>Saved compositions</h2></div>
              <div className="picker-list">
                {drafts.data?.map((x) => (
                  <button key={x.id} type="button" className={`picker-row${x.id === draftId ? ' on' : ''}`}
                    onClick={() => { setDraftId(x.id); setRenderId(null); setLoadOpen(false); }}>
                    <span className="dd-label">{x.name ?? (x.briefText.slice(0, 40) || 'untitled draft')}</span>
                    <span className="sub dd-n">{x.slots} clips · {new Date(x.updatedAt).toLocaleDateString()}</span>
                    {x.lastRender
                      ? <span className={`chip ${x.lastRender.status === 'done' ? 'ok' : x.lastRender.status === 'failed' ? 'bad' : ''}`}>
                        {x.lastRender.kind === 'final' ? 'final' : 'draft'} render · {x.lastRender.status} · {new Date(x.lastRender.createdAt).toLocaleDateString()}
                      </span>
                      : <span className="chip">never rendered</span>}
                  </button>
                ))}
                {(drafts.data?.length ?? 0) === 0 && <div className="empty">No saved compositions yet.</div>}
              </div>
              <div className="row between">
                <button type="button" className="btn quiet" onClick={() => setLoadOpen(false)}>Close</button>
              </div>
            </div>
          </>
        )}
        {!d && (
          <div className="compose-hero">
            <form className="compose-hero-box" onSubmit={submitBrief}>
              <textarea autoFocus rows={3} value={brief} maxLength={500} aria-label="Describe the edit you want"
                placeholder={heroPlaceholder}
                onChange={(e) => setBrief(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !assembling) {
                    e.preventDefault();
                    void generate(null, '9:16');
                  }
                }} />
              <div className="row compose-hero-foot">
                <span className="sub compose-hero-note" role="status">{assembling ? <GeneratePhrase /> : audioNote ?? ''}</span>
                <button type="submit" className="btn primary lg compose-hero-go" disabled={assembling}>
                  <Spark className="spark spark-inverse" /> {assembling ? 'Assembling…' : 'Generate'}
                </button>
              </div>
            </form>
          </div>
        )}
        {!d && (
          <Recents moreTo="/archive" onOpen={(id) => { setDraftId(id); setRenderId(null); }}
            items={(drafts.data ?? []).map((x) => ({ id: x.id, name: x.name ?? (x.briefText.slice(0, 40) || 'untitled draft'), posterUrl: x.posterUrl }))} />
        )}
        {d && generateOpen && (
          <>
            <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setGenerateOpen(false)} />
            <div className="modal generate-modal" role="dialog" aria-label="Generate a new cut">
              <h2><Spark className="spark" /> Generate</h2>
              <textarea autoFocus rows={3} value={brief} maxLength={500} aria-label="Describe the edit you want"
                placeholder={heroPlaceholder} className="approve-caption"
                onChange={(e) => setBrief(e.target.value)} />
              <span className="sub">Every slot regenerates from the library; the current arrangement is replaced, and this composition stays saved in the Archive. Leave it blank and the system invents a concept from the brand&apos;s footage.</span>
              <div className="row between">
                <button type="button" className="btn quiet" onClick={() => setGenerateOpen(false)}>Cancel</button>
                <div className="row">
                  {assembling && <span className="sub" role="status"><GeneratePhrase /></span>}
                  <button type="button" className="btn primary" disabled={assembling}
                    onClick={() => void generate(d.id, d.format)}>
                    {assembling ? 'Assembling…' : 'Generate'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
        {plan.isError && <div className="card pad error" role="alert">{plan.error.message}</div>}
        {autoBrief.isError && <div className="card pad error" role="alert">{autoBrief.error.message}</div>}
        {refreshSlot.isError && <div className="row"><span className="chip warn" role="status">{refreshSlot.error.message}</span></div>}
        {d && d.plan && (() => {
          const close = d.slots.filter((s) => s.match === 'close' || s.match === 'taken').length;
          const exact = d.slots.length - close;
          return (
            <div className="row match-status">
              <span className="chip ok">{exact} clip{exact === 1 ? '' : 's'} found</span>
              {close > 0 && <span className="chip warn">{close} matched on close semantics, the nearest the library has</span>}
            </div>
          );
        })()}
        {plan.data && plan.data.unfilled.length > 0 && (
          <div className="row"><span className="chip warn">Slot{plan.data.unfilled.length > 1 ? 's' : ''} {plan.data.unfilled.join(', ')} unfilled: nothing in the library matched</span></div>
        )}

        {d && (
          <div className="compose-body">
            <div className="compose-left">
              <div className="row snips-head">
                <div className="h2">Timeline{d.plan && <AutoPill>auto</AutoPill>}</div>
                <span className="sub snips-hint">Trim on the waveform · ↻ swaps one clip · the prompt replaces with what you ask for</span>
              </div>
              <div className={`slot-grid z${zoomLevel}`}>
                {(dragOrder ?? d.slots.map((_, i) => i)).map((si, vi) => {
                  const slot = d.slots[si];
                  if (!slot) return null;
                  const meta = d.slotMeta[si];
                  const order = dragOrder ?? d.slots.map((_, i) => i);
                  const clipHeard = slot.audio !== 'mute' && channelHeard('clips', d.mutes, d.solos);
                  // What the card shows while a trim handle or a slip is being dragged, here or on the strip.
                  const slipped = slipDrafts[si] !== undefined ? { inMs: slot.inMs + slipDrafts[si]!, outMs: slot.outMs + slipDrafts[si]! } : null;
                  const staged = stagedSlots[si] ?? slipped;
                  return (
                    <div key={`${slot.snippetId}-${si}`} className={`slot-card${dragIndex === si ? ' dragging' : ''}${textSlot === si ? ' text-target' : ''}`}
                      onDragOver={(e) => {
                        // The grid shuffles live behind the dragged card; release just confirms it.
                        e.preventDefault();
                        if (dragIndex === null) return;
                        const cur = order.indexOf(dragIndex);
                        if (cur === vi) return;
                        const next = [...order];
                        next.splice(cur, 1);
                        next.splice(vi, 0, dragIndex);
                        setDragOrder(next);
                      }}
                      onDrop={(e) => e.preventDefault()}>
                      <div className="slot-role">
                        {/* One line, cut short; the hover title carries the whole name. */}
                        <b title={meta?.title ?? meta?.role ?? 'Clip removed'}>{meta?.title ?? meta?.role ?? 'Clip removed'}</b>
                        {slot.match === 'close' && <span className="chip warn slot-close" title="Nothing in the library shows what was asked; this is the nearest by meaning">close match</span>}
                        {slot.match === 'taken' && <span className="chip warn slot-close" title="The clip that shows what was asked is already in this edit; this is the nearest other">already used</span>}
                        <span className="slot-grip" draggable role="button" aria-label="Drag to reorder" title="Drag to reorder"
                          onDragStart={(e) => { setDragIndex(si); setDragOrder(order); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragEnd={(e) => {
                            if (e.dataTransfer.dropEffect !== 'none' && dragOrder) commitOrder(dragOrder);
                            setDragIndex(null);
                            setDragOrder(null);
                          }}>
                          <Icon name="grip" />
                        </span>
                        <span className="sub slot-len">{meta?.creatorName ? `${meta.creatorName} · ` : ''}{((slot.outMs - slot.inMs) / 1000).toFixed(1)} s</span>
                      </div>
                      <button type="button" className="slot-th" onClick={() => startPreview(si, true)} aria-label={`Play slot ${vi + 1}`}>
                        {meta?.proxyUrl || meta?.posterUrl
                          ? <SlotThumb proxyUrl={meta.proxyUrl} posterUrl={meta.posterUrl}
                            timeMs={stagedSlots[si] && activeEdge[si] === 'out'
                              ? Math.max((stagedSlots[si]!.inMs), stagedSlots[si]!.outMs - 100)
                              : (staged ?? slot).inMs} />
                          : <span className="slot-empty">Empty holder<small>Timing kept · ↻ fills it</small></span>}
                        {slot.overlay.length > 0 && <TextOverlay lines={slot.overlay} />}
                        {meta && <FreshnessBadge freshness={meta.freshness} />}
                        <span className="slot-dur mono">{((slot.outMs - slot.inMs) / 1000).toFixed(1)}s</span>
                      </button>
                      {/* Two panels, each track's waveform with its own controls (Dax, 2026-09-29): the clip in the
                          pale blue of its waveform, its sound effect in the pale brown of its own. */}
                      <div className="slot-track clip">
                      {meta && (
                        // A clip whose own sound is not heard greys out at its full height, so nothing on the page
                        // moves when a clip or the timeline is muted (Dax, 2026-09-30). A clip with no sound at all
                        // still gets its waveform, flat, because the in and out handles live on it (Dax, 2026-10-01).
                        <div className={`slot-wave${clipHeard ? '' : ' dim'}`} title={panelNote('clips') ?? undefined}>
                          <Waveform peaks={meta.peaks} durationMs={meta.boundsOutMs} windowStartMs={meta.boundsInMs} windowEndMs={meta.boundsOutMs}
                            regions={[]} words={meta.words} showWords sceneCuts={[]} height={SLOT_WAVE_H}
                            ranges={[{ id: 'slot', inMs: slot.inMs, outMs: slot.outMs, colour: (clipTints[si] ?? clipColours(si, slotCount)).accent, excluded: false }]}
                            selectedId="slot" staged={staged} playheadMs={null}
                            onSlip={(ms, done) => dragSlip(si, ms, done)}
                            onEdge={(which, ms, done) => {
                              const cur = stagedSlots[si] ?? { inMs: slot.inMs, outMs: slot.outMs };
                              const next = which === 'in'
                                ? { ...cur, inMs: Math.max(meta.boundsInMs, Math.min(ms, cur.outMs - 200)) }
                                : { ...cur, outMs: Math.min(meta.boundsOutMs, Math.max(ms, cur.inMs + 200)) };
                              if (done) {
                                commitSlot(si, next);
                                setActiveEdge((e) => {
                                  const { [si]: gone, ...rest } = e;
                                  void gone;
                                  return rest;
                                });
                              } else {
                                setActiveEdge((e) => (e[si] === which ? e : { ...e, [si]: which }));
                                setStagedSlots((s) => ({ ...s, [si]: next }));
                              }
                            }} />
                        </div>
                      )}
                      <div className="row slot-tools">
                        <Toolbar dense>
                          <Tool icon="redo" title="Next match" disabled={refreshSlot.isPending}
                            onClick={() => refreshSlot.mutate({ draftId: d.id, slotIndex: si, prompt: null })} />
                          <Tool sparkle title="Replace with a described clip" active={promptOpen === si}
                            onClick={() => setPromptOpen(promptOpen === si ? null : si)} />
                          {/* Jitter lives in the menu: the row keeps room for the clip's knob (Dax, 2026-09-29). */}
                          <Tool icon="text" title="Add text overlay" active={textSlot === si || slot.overlay.length > 0}
                            onClick={() => (textSlot === si ? closeTextSlot() : openTextSlot(si))} />
                          <Tool icon="reset" title="Reset: the clip's full range, audio back to default"
                            onClick={() => { if (meta) setSlot(si, { inMs: meta.boundsInMs, outMs: meta.boundsOutMs, gainDb: 0, audio: meta.muted ? 'mute' : 'keep', audio2: null }); }} />
                          <Tool icon="trash" title="Remove this clip" danger onClick={() => removeSlot(si)} />
                        </Toolbar>
                        <Knob label="Clip volume" min={GAIN_MIN} max={GAIN_MAX} value={{ db: slot.gainDb, muted: slot.audio === 'mute' }}
                          dimmed={panelNote('clips')}
                          onChange={(v, done) => {
                            if (!done) return;
                            setSlot(si, { gainDb: v.db, audio: v.muted ? 'mute' : slot.audio === 'mute' ? 'keep' : slot.audio });
                            if (v.db !== slot.gainDb) offByHand('clips');
                          }} />
                        <RowMenu title="Clip actions" items={[
                          rateItem('snippet', slot.snippetId),
                          null,
                          { label: 'Next match (swap this clip)', onClick: () => refreshSlot.mutate({ draftId: d.id, slotIndex: si, prompt: null }) },
                          { label: 'Replace by prompt…', onClick: () => setPromptOpen(si) },
                          // Meaning picks; freshness is the person's call (2026-10-04). Nothing is fresher than never used.
                          ...(meta && meta.freshness !== 'new' ? [{
                            label: 'Find a fresher clip',
                            content: <span className="fresh-item"><FreshnessDot freshness={meta.freshness} />Find a fresher clip</span>,
                            onClick: () => refreshSlot.mutate({ draftId: d.id, slotIndex: si, prompt: null, fresher: true }),
                          }] : []),
                          { label: 'Jitter start and end', onClick: () => setSlot(si, jitterSlot(slot, meta ? { boundsInMs: meta.boundsInMs, boundsOutMs: meta.boundsOutMs } : null)) },
                          { label: slot.overlay.length ? 'Edit text overlay' : 'Add text overlay', onClick: () => openTextSlot(si) },
                          null,
                          { label: slot.audio === 'mute' ? 'Unmute the clip\'s own sound' : 'Mute the clip\'s own sound', onClick: () => setSlot(si, { audio: slot.audio === 'mute' ? 'keep' : 'mute' }) },
                          // With the ASMR channel off there is no sound row yet: this is how one clip gets a sound.
                          ...(asmrOn ? [] : [{ label: 'Find a sound for this clip', onClick: () => refreshAudio.mutate({ draftId: d.id, slotIndex: si, prompt: null }) }]),
                          null,
                          { label: 'Remove this clip', danger: true, onClick: () => removeSlot(si) },
                        ]} />
                      </div>
                      {promptOpen === si && (
                        <div className="slot-prompt-holder">
                          <input autoFocus className="slot-replace" placeholder="Describe a clip (Enter swaps it)" aria-label="Replace slot with" value={slotPrompts[si] ?? ''}
                            onChange={(e) => setSlotPrompts((p) => ({ ...p, [si]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && (slotPrompts[si] ?? '').trim()) {
                                refreshSlot.mutate({ draftId: d.id, slotIndex: si, prompt: slotPrompts[si]!.trim() });
                                setSlotPrompts((p) => ({ ...p, [si]: '' }));
                                setPromptOpen(null);
                              }
                              if (e.key === 'Escape') setPromptOpen(null);
                            }} />
                        </div>
                      )}
                      </div>
                      {asmrOn && (
                        <div className="slot-track sfx">
                        <div className={`slot-sound${!channelHeard('asmr', d.mutes, d.solos) || slot.audio2?.muted ? ' off' : ''}`} title={panelNote('asmr') ?? undefined}>
                          {slot.audio2 && meta?.sound ? (
                            <SoundLane peaks={meta.sound.peaks} durationMs={meta.sound.durationMs} windowMs={slot.outMs - slot.inMs}
                              heard={trimDrafts[si] ?? { fromMs: slot.audio2.trimFromMs, toMs: slot.audio2.trimToMs ?? meta.sound.durationMs }}
                              {...(clipTints[si] ? { colours: clipTints[si] } : {})}
                              offsetMs={offsetDrafts[si] ?? slot.audio2.offsetMs} muted={!channelHeard('asmr', d.mutes, d.solos) || slot.audio2.muted}
                              onNudge={(ms, done) => {
                                if (!done) {
                                  setOffsetDrafts((o) => ({ ...o, [si]: ms }));
                                  return;
                                }
                                setOffsetDrafts((o) => {
                                  const { [si]: gone, ...rest } = o;
                                  void gone;
                                  return rest;
                                });
                                if (ms !== slot.audio2!.offsetMs) setSlot(si, { audio2: { ...slot.audio2!, offsetMs: ms } });
                              }} />
                          ) : <span className="sound-lane sound-lane-empty sub">{slot.audio2 ? 'Loading…' : 'No sound'}</span>}
                        </div>
                        <div className={`row slot-sound-bar${!channelHeard('asmr', d.mutes, d.solos) || slot.audio2?.muted ? ' off' : ''}`}>
                          <Toolbar dense>
                            <Tool icon="redo" title="Replace the sound: the next one that fits this clip" disabled={refreshAudio.isPending}
                              onClick={() => refreshAudio.mutate({ draftId: d.id, slotIndex: si, prompt: null })} />
                            <Tool sparkle title="Describe the sound for this clip" active={soundPromptOpen === si}
                              onClick={() => { setSoundPrompt(''); setSoundPromptOpen(soundPromptOpen === si ? null : si); }} />
                          </Toolbar>
                          <span className="sub slot-a2-name" title={meta?.sound?.name ?? ''}>{meta?.sound?.name ?? ''}</span>
                          {slot.audio2?.match === 'close' && <span className="chip warn slot-a2-close" title="The nearest by meaning; nothing matched the words exactly">close</span>}
                          {/* Always a knob and a menu, so this row's line up under the clip row's (Dax, 2026-09-30). */}
                          <Knob label="Sound effect volume" min={GAIN_MIN} max={GAIN_MAX} value={{ db: slot.audio2?.gainDb ?? 0, muted: slot.audio2?.muted ?? false }}
                            disabled={!slot.audio2} dimmed={panelNote('asmr')}
                            onChange={(v, done) => {
                              if (!done || !slot.audio2) return;
                              setSlot(si, { audio2: { ...slot.audio2, gainDb: v.db, muted: v.muted } });
                              if (v.db !== slot.audio2.gainDb) offByHand('asmr');
                            }} />
                          <RowMenu title="Sound actions" items={[
                            ...(slot.audio2 ? [rateItem('audio_bed', slot.audio2.bedId), null] : []),
                            { label: 'Describe a new sound…', onClick: () => { setSoundPrompt(''); setSoundPromptOpen(si); } },
                            { label: slot.audio2 ? 'Replace with a similar sound' : 'Find a sound for this clip', onClick: () => refreshAudio.mutate({ draftId: d.id, slotIndex: si, prompt: null }) },
                            { label: slot.audio2?.muted ? 'Unmute the sound' : 'Mute the sound', disabled: !slot.audio2, onClick: () => { if (slot.audio2) setSlot(si, { audio2: { ...slot.audio2, muted: !slot.audio2.muted } }); } },
                            { label: 'Browse audio for this clip', onClick: () => void navigate({ to: '/browse', search: { tab: 'audio', pickSlot: si } }) },
                            null,
                            { label: 'Remove the sound', danger: true, disabled: !slot.audio2, onClick: () => setSlot(si, { audio2: null }) },
                          ]} />
                        </div>
                      {soundPromptOpen === si && (
                        <input autoFocus className="slot-replace" placeholder="Describe the sound (Enter fetches it)" aria-label="Describe the sound for this clip" value={soundPrompt}
                          onChange={(e) => setSoundPrompt(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && soundPrompt.trim()) {
                              refreshAudio.mutate({ draftId: d.id, slotIndex: si, prompt: soundPrompt.trim() });
                              setSoundPromptOpen(null);
                            }
                            if (e.key === 'Escape') setSoundPromptOpen(null);
                          }} />
                      )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="slot-card slot-add">
                  <Link to="/browse" className="slot-add-plus" title="Browse the library for the next clip" aria-label="Add a clip from Browse">
                    <span className="slot-add-glyph">+</span>
                    <span className="tool-label">Browse</span>
                  </Link>
                  <button type="button" className={`slot-add-plus${instructOpen ? ' on' : ''}`} title="Describe what comes next, or a whole rearrangement" onClick={() => setInstructOpen(!instructOpen)}>
                    <Spark className="slot-add-spark" />
                    <span className="tool-label">Ask</span>
                  </button>
                </div>
              </div>
              {instructOpen && (
                <form className="card ask-bar compose-instruct" onSubmit={(e) => { e.preventDefault(); if (instruction.trim().length >= 3) instruct.mutate({ draftId: d.id, instruction: instruction.trim() }); }}>
                  <span className="ask-spark" aria-hidden="true"><Spark /></span>
                  <input autoFocus value={instruction} onChange={(e) => setInstruction(e.target.value)}
                    placeholder="e.g. bring in three more peanut clips, move the first two forward, new ones in slots 2 and 3" aria-label="Rearrange by instruction" />
                  <button type="submit" className="btn primary" disabled={instruct.isPending || instruction.trim().length < 3}>{instruct.isPending ? 'Arranging…' : 'Apply'}</button>
                </form>
              )}
              {instruct.isError && <div className="error" role="alert">{instruct.error.message}</div>}
              {instruct.data && instruct.data.unfilled.length > 0 && (
                <div className="row">{instruct.data.unfilled.map((q) => <span key={q} className="chip warn">nothing matched &quot;{q}&quot;</span>)}</div>
              )}
              {refreshSlot.isError && <div className="error" role="alert">{refreshSlot.error.message}</div>}

              {audioDesk()}

              <div className="card captions-card">
                <button type="button" className="section-head" aria-expanded={capsOpen} onClick={() => setCapsOpen(!capsOpen)}>
                  <span className="section-title">Captions</span>
                  {!capsOpen && <span className="sub section-hint">Click to expand</span>}
                  <Chevron up={capsOpen} />
                </button>
                {capsOpen && (
                  <>
                <div className="audio-row">
                  <span className="compose-row-icon" aria-hidden="true">Tt</span>
                  <div className="compose-row-name"><b>Style</b><span className="sub">Burned in at render</span></div>
                  <div className="seg">
                    <button type="button" className={d.text.mode !== 'captions' ? 'on' : ''}
                      onClick={() => update.mutate({ draftId: d.id, text: { ...d.text, mode: 'none', style: null, title: null } })}>None</button>
                    {CaptionStyle.options.map((s) => (
                      <button key={s} type="button" className={d.text.mode === 'captions' && d.text.style === s ? 'on' : ''}
                        onClick={() => update.mutate({ draftId: d.id, text: { ...d.text, mode: 'captions', style: s, title: null } })}>{s === 'singalong' ? 'Sing-along' : s[0]!.toUpperCase() + s.slice(1)}</button>
                    ))}
                  </div>
                  {d.text.mode === 'captions' && d.text.style && (
                    <span className={`cap-sample cap-${d.text.style}`} aria-hidden="true">zero sugar{d.text.style === 'singalong' ? ', ' : ' '}<i>all joy</i></span>
                  )}
                </div>
                {d.text.mode === 'captions' && (
                  <div className="audio-row cap-style-row">
                    <label className="tool-field">
                      <span className="tool-label">Font</span>
                      <select value={d.text.capStyle.font ?? ''} aria-label="Caption font"
                        onChange={(e) => update.mutate({ draftId: d.id, text: { ...d.text, capStyle: { ...d.text.capStyle, font: e.target.value || null } } })}>
                        <option value="">Preset font</option>
                        {TEXT_FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                      </select>
                    </label>
                    <label className="tool-field narrow">
                      <span className="tool-label">Size %</span>
                      <input type="number" min={1} max={15} step={0.5} value={d.text.capStyle.sizePct ?? ''} placeholder="preset" aria-label="Caption size percent"
                        onChange={(e) => update.mutate({ draftId: d.id, text: { ...d.text, capStyle: { ...d.text.capStyle, sizePct: e.target.value ? Number(e.target.value) : null } } })} />
                    </label>
                    <label className="tool-field narrow">
                      <span className="tool-label">Colour</span>
                      <input type="color" value={d.text.capStyle.colour ?? '#ffffff'} aria-label="Caption colour"
                        onChange={(e) => update.mutate({ draftId: d.id, text: { ...d.text, capStyle: { ...d.text.capStyle, colour: e.target.value } } })} />
                    </label>
                    <label className="tool-field">
                      <span className="tool-label">Outline {d.text.capStyle.outlineW ?? 'preset'}</span>
                      <input type="range" min={0} max={OUTLINE_STOPS.length - 1} step={1} className="gain fx-gain"
                        value={outlineStopIndex(d.text.capStyle.outlineW)} aria-label="Caption outline width"
                        onChange={(e) => patchDraft({ text: { ...d.text, capStyle: { ...d.text.capStyle, outlineW: OUTLINE_STOPS[Number(e.target.value)]! } } })}
                        onPointerUp={(e) => update.mutate({ draftId: d.id, text: { ...d.text, capStyle: { ...d.text.capStyle, outlineW: OUTLINE_STOPS[Number((e.target as HTMLInputElement).value)]! } } })} />
                    </label>
                    <label className="tool-field narrow">
                      <span className="tool-label">Outline colour</span>
                      <input type="color" value={d.text.capStyle.outlineColour ?? '#000000'} aria-label="Caption outline colour"
                        onChange={(e) => update.mutate({ draftId: d.id, text: { ...d.text, capStyle: { ...d.text.capStyle, outlineColour: e.target.value } } })} />
                    </label>
                    <button type="button" className={`chip${d.text.capStyle.shadow ? ' acc' : ''}`}
                      onClick={() => update.mutate({ draftId: d.id, text: { ...d.text, capStyle: { ...d.text.capStyle, shadow: !(d.text.capStyle.shadow ?? false) } } })}>Shadow</button>
                    <label className="tool-field">
                      <span className="tool-label">Position {d.text.capStyle.yPct ?? 'preset'}%</span>
                      <input type="range" min={2} max={98} step={1} className="gain fx-gain"
                        value={d.text.capStyle.yPct ?? 84} aria-label="Caption vertical position"
                        onChange={(e) => patchDraft({ text: { ...d.text, capStyle: { ...d.text.capStyle, yPct: Number(e.target.value) } } })}
                        onPointerUp={(e) => update.mutate({ draftId: d.id, text: { ...d.text, capStyle: { ...d.text.capStyle, yPct: Number((e.target as HTMLInputElement).value) } } })} />
                    </label>
                  </div>
                )}
                {d.text.mode === 'captions' && (
                  <div className="captions-body">
                    <textarea className="captions-script" rows={3} maxLength={4000}
                      value={scriptDraft ?? d.text.script ?? captionScript.data?.script ?? ''}
                      placeholder={captionScript.isLoading ? 'Reading the script…' : 'No spoken words detected in these clips'}
                      aria-label="Caption script"
                      onChange={(e) => setScriptDraft(e.target.value)} />
                    <div className="row between captions-foot">
                      <span className="sub captions-note">The full detected script, start to end; fix words, spelling and punctuation. Trimming a clip re-fits the wording to the new windows on the next generate.</span>
                      <div className="row">
                        <button type="button" className="btn quiet"
                          onClick={() => { setScriptDraft(captionScript.data?.script ?? ''); update.mutate({ draftId: d.id, text: { ...d.text, script: null } }); }}>Reset to detected</button>
                        <button type="button" className="btn primary"
                          onClick={() => {
                            const s = (scriptDraft ?? d.text.script ?? captionScript.data?.script ?? '').trim();
                            update.mutate({ draftId: d.id, text: { ...d.text, script: s && s !== captionScript.data?.script ? s : null } });
                          }}>Generate captions</button>
                      </div>
                    </div>
                  </div>
                )}
                  </>
                )}
              </div>
              <div className="sub mono compose-tech">{d.slots.length} clips · {(totalMs / 1000).toFixed(1)} s</div>
            </div>

            <div className="compose-right">
              <div className="card compose-preview">
                <div className={`preview-frame f-${d.format.replace(':', '-')}`}>
                  {/* crossOrigin: the live mix can only hear media served with CORS. */}
                  <video ref={videoRef} className={`preview-video${frontVideo === 0 ? '' : ' standby'}`} playsInline preload="auto" crossOrigin="anonymous"
                    onPlay={onVideoPlay} onPause={onVideoPause} poster={d.slotMeta[0]?.posterUrl ?? undefined} />
                  <video ref={altVideoRef} className={`preview-video${frontVideo === 1 ? '' : ' standby'}`} playsInline preload="auto" crossOrigin="anonymous"
                    onPlay={onVideoPlay} onPause={onVideoPause} />
                  {!previewPlaying && !previewBusy && (
                    <button type="button" className="preview-play" aria-label="Play the cut" onClick={playPreview}>
                      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 3.5v9l7-4.5z" /></svg>
                    </button>
                  )}
                  {previewBusy && (
                    <div className="preview-busy" role="status" aria-label="Burning your text into the preview">
                      <span className="spinner" />
                    </div>
                  )}
                </div>
                <PreviewScrubber totalMs={totalMs} position={positionMs} onSeek={seekPreview} />
                <div className="row compose-preview-bar">
                  <button type="button" className="pv-btn" aria-label="Play" title="Play the cut" disabled={!d.slots.length} onClick={playPreview}><Icon name="play" /></button>
                  <button type="button" className="pv-btn" aria-label="Pause" title="Pause" onClick={pausePreview}><Icon name="pause" /></button>
                  <button type="button" className="pv-btn" aria-label="Stop" title="Stop and return to the first frame" onClick={stopPreview}><Icon name="stop" /></button>
                  <button type="button" className="pv-btn pv-render" title="Export the final video, cut from the masters"
                    disabled={queueRender.isPending || render.data?.status === 'queued' || render.data?.status === 'rendering'}
                    onClick={() => queueRender.mutate({ draftId: d.id, kind: 'final' })}><Icon name="export" /> Export</button>
                  {render.data && <RenderPill status={render.data.status} stage={render.data.stage} progress={render.data.progress} etaMs={render.data.etaMs} />}
                  {render.data?.status === 'done' && render.data.publicUrl && (
                    <a className="link-btn" href={render.data.publicUrl} target="_blank" rel="noreferrer">Open</a>
                  )}
                  {previewSlot !== null && <span className="sub">slot {previewSlot + 1} of {d.slots.length}</span>}
                  <span className={`sub mono pv-dur${durFlash ? ' flash' : ''}`}>{(totalMs / 1000).toFixed(1)} s</span>
                </div>
                {render.data?.status === 'failed' && render.data.error && <div className="error" role="alert">{render.data.error}</div>}
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
